/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */
import { Box, Grid, TextareaAutosize, IconButton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import SendIcon from '@mui/icons-material/Send';
import { useEffect, useState } from 'react';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import { searchActions } from '../slice';
import { selectLoader, selectSearchData, selectSelectedScenario } from '../selectors';
import VisualView from './visuals/visual-view';
import { SEARCH_INPUT_STYLE, SEARCH_TEXT_AREA_STYLE, SEND_BUTTON_STYLE } from '../style';
import ScenarioList from './scenarios-list';
import { selectTabList } from '../../../../selectors';

export default function SearchWidget({ value, active, latestDate }) {
  const [searchText, setSearchText] = useState('');
  const dispatch = useDispatch();
  const loading = useSelector(selectLoader);
  const searchData = useSelector(selectSearchData);
  const selectedScenario = useSelector(selectSelectedScenario);
  const organizationId = useSelector(selectOrganizationId);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const handleSend = () => {
    if (!searchText) return;
    dispatch(
      searchActions.getSearchScenarioData({
        organizationId,
        query: `search=${searchText}`,
      })
    );
  };
  // update selected scenario when changing filters
  useEffect(() => {
    if (selectedScenario && searchData?.length) {
      const found = searchData?.find((s) => s.id === selectedScenario.id);
      dispatch(searchActions.setSelectedScenario(found));
    }
  }, [searchData]);
  //
  const clearSelectedScenario = () => dispatch(searchActions.setSelectedScenario(null));
  //
  if (!active) {
    return null;
  }
  //
  return selectedScenario ? (
    <Grid
      container
      sx={{
        display: value === TAB_VALUE.SEARCH ? '' : 'none',
      }}
      className={styles.mainGrid}
      direction="row"
      alignItems="flex-start"
    >
      <VisualView clearScenario={clearSelectedScenario} latestDate={latestDate} />
    </Grid>
  ) : (
    <Grid
      container
      sx={{
        display: value === TAB_VALUE.SEARCH ? '' : 'none',
      }}
      spacing={4}
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="end"
        gap={12}
        width="100%"
        minHeight={280}
        py={16}
      >
        <Box>
          {/* Search Input */}
          <Box display="flex" justifyContent="center" sx={SEARCH_INPUT_STYLE}>
            <TextareaAutosize
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search scenarios..."
              variant="outlined"
              minRows={3}
              maxRows={3}
              style={SEARCH_TEXT_AREA_STYLE}
            />
            {/* Send Button */}
            <IconButton
              onClick={handleSend}
              disabled={!searchText || loading}
              sx={SEND_BUTTON_STYLE}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
      <ScenarioList />
    </Grid>
  );
}
